import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ui } from '@procuros/datachecks';
import type { SuccessfulEnrichment } from 'hooks/useGruAgent/types';

import { Sidebar } from 'components/Sidebar/Sidebar';
import { EnrichmentActionDetails } from 'components/WebEDI/EnrichmentActionDetails/EnrichmentActionDetails';
import { PDFViewerPanel } from 'components/WebEDI/PDFViewerPanel/PDFViewerPanel';
import { ResizablePanelHandle } from 'components/WebEDI/WebEDISidebar/components/ResizablePanelHandle';
import { TabBar, TabBarItem } from 'components/WebEDI/WebEDISidebar/components/TabBar';
import { useResizablePanel } from 'components/WebEDI/WebEDISidebar/useResizablePanel';

type EditSuggestionsSidebarTab = 'ai_agent' | 'view_pdf';

type EditSuggestionsSidebarProps = {
  agentProposal: SuccessfulEnrichment;
  uiConfig: Array<ui.Section> | undefined;
  canonical: Record<string, unknown> | undefined | null;
  pdfUrl?: string;
  pdfFilename?: string;
};

const BASE_SIDEBAR_WIDTH = 360;

export const EditSuggestionsSidebar = ({
  agentProposal,
  uiConfig,
  canonical,
  pdfUrl,
  pdfFilename,
}: EditSuggestionsSidebarProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<EditSuggestionsSidebarTab>('ai_agent');

  const hasPDF = !!pdfUrl && !!pdfFilename;
  const isInPDFTab = hasPDF && activeTab === 'view_pdf';

  const { panelWidth, handleMouseDown, collapse, restoreNav } = useResizablePanel(isInPDFTab, {
    initialWidth: BASE_SIDEBAR_WIDTH,
    minWidth: BASE_SIDEBAR_WIDTH,
  });

  const handleClickTab = useCallback(
    (id: EditSuggestionsSidebarTab) => {
      collapse();
      if (id === 'ai_agent') {
        restoreNav();
      }
      setActiveTab(id);
    },
    [collapse, restoreNav],
  );

  const tabs: Array<TabBarItem<EditSuggestionsSidebarTab>> = hasPDF
    ? [
        { id: 'ai_agent', label: t('webedi:sidebar.tabs.agents') },
        { id: 'view_pdf', label: t('webedi:sidebar.tabs.viewPDF') },
      ]
    : [];

  return (
    <Sidebar
      width={isInPDFTab ? panelWidth : BASE_SIDEBAR_WIDTH}
      header={hasPDF ? <TabBar tabs={tabs} activeTab={activeTab} onClickTab={handleClickTab} /> : undefined}
      overlay={isInPDFTab ? <ResizablePanelHandle onMouseDown={handleMouseDown} /> : null}
      contentLayout={isInPDFTab ? 'flush' : 'padded'}
      activeTabId={hasPDF ? activeTab : undefined}
    >
      {isInPDFTab && pdfUrl && pdfFilename ? (
        <PDFViewerPanel pdfUrl={pdfUrl} filename={pdfFilename} showClose={false} showDownload />
      ) : (
        <div>
          <p className="mb-3 text-sm leading-5 font-normal text-gray-900">
            {t('webedi:sidebar.agents.editIntro', { count: agentProposal.payload.enriched_count })}
          </p>
          <EnrichmentActionDetails agentProposal={agentProposal} uiConfig={uiConfig} canonical={canonical} />
        </div>
      )}
    </Sidebar>
  );
};
